// DayView.jsx

import React, { useLayoutEffect, useMemo, useRef } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';

// Utilities
import { normalizeDateValue } from 'utilities/DateTime';
import { groupAllDayEventsByDateKey, groupTimedEventsByDateHour } from 'views/calendar/shared/calendarUtils';

// Hooks
import useCalendarDateFields from 'views/calendar/hooks/useCalendarDateFields';
import useCalendarEvents from 'views/calendar/hooks/useCalendarEvents';

// CSS
import './DayView.css';

// Components
import { addDays, isSameDay, startOfDay } from 'date-fns';
import CalendarNavControls from 'views/calendar/shared/CalendarNavControls';
import CalendarRangePanel from 'views/calendar/shared/CalendarRangePanel';

/**
 * DayView
 * 
 * Description
 *  
 *
 * @returns {ReactNode} - DayView.
 */
const DayView = React.memo(({
    view,
    typeDef,
    nodes = [],
    eventItems,
    language = 'en',
    onNodeClick,
    onNodeDoubleClick,
    onAddDate,
    onAddDateTime,
    canDragEvents = false,
    draggingItem = null,
    dragOverTargetKey = null,
    onEventDragStart,
    onEventDragEnd,
    onDropTargetDragOver,
    onMoveToTarget,
}) => {

    // ----- CONTEXT ----- //

    const {
        selectedDate,
        setSelectedDate,
    } = useCalendarTimeline();

    // ----- REFS ----- //

    const containerRef = useRef(null);

    // ----- HOOKS ----- //

    const { typeKey, dateFields } = useCalendarDateFields(typeDef, { preferredFieldKey: view?.calendarField });

    // ----- MEMOS ----- //

    const baseDate = useMemo(
        () => normalizeDateValue(selectedDate) || new Date(),
        [selectedDate]
    );

    const rangeStart = useMemo(
        () => startOfDay(baseDate),
        [baseDate]
    );

    const rangeDays = useMemo(() => [rangeStart], [rangeStart]);

    const today = useMemo(() => new Date(), []);

    const rangeLabel = useMemo(() => (
        rangeStart.toLocaleDateString(language || 'en', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        })
    ), [rangeStart, language]);

    // ----- HOOKS ----- //

    const resolvedEventItems = useCalendarEvents({
        nodes,
        dateFields,
        typeKey,
        eventItems,
        rangeStart,
        rangeEnd: rangeStart,
    });

    // ----- MEMOS ----- //

    const allDayEventsByDate = useMemo(
        () => groupAllDayEventsByDateKey(resolvedEventItems, (item) => isSameDay(item.day, rangeStart)),
        [resolvedEventItems, rangeStart]
    );

    const timedEventsByDateHour = useMemo(
        () => groupTimedEventsByDateHour(resolvedEventItems, (item) => isSameDay(item.day, rangeStart)),
        [resolvedEventItems, rangeStart]
    );

    // ----- HANDLERS ----- //

    /**
 * Handles range change.
 *
 * @param {*} direction
 */

    const handleRangeChange = (direction) => {
        const nextDate = addDays(baseDate, direction);
        setSelectedDate(nextDate);
    };

    /**
 * Handles go to today.
 */
    const handleGoToToday = () => {
        setSelectedDate(new Date());
    };

    // ----- EFFECTS ----- //

    useLayoutEffect(() => {
        const container = containerRef.current;
        const grid = container?.querySelector('.calendar-range-panel-grid');
        const scrollParent = container?.parentElement;
        if (!container || !grid || !scrollParent) return undefined;

        const syncHourRowMinHeight = () => {
            const availableHeight = scrollParent.clientHeight - grid.offsetTop;
            const hourRowMinHeight = Math.max(40, Math.floor(availableHeight / 24));
            container.style.setProperty('--calendar-range-hour-row-min', `${hourRowMinHeight}px`);
        };

        syncHourRowMinHeight();

        const resizeObserver = new ResizeObserver(syncHourRowMinHeight);
        resizeObserver.observe(scrollParent);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    // ----- RENDER ----- //

    return (

        <div
          className="day-view-container"
          style={{
            '--calendar-range-columns': 1,
          }}
          ref={containerRef}
        >
            <CalendarNavControls
              title={rangeLabel}
              prevAriaLabel="Previous day"
              nextAriaLabel="Next day"
              onNext={() => handleRangeChange(1)}
              onPrev={() => handleRangeChange(-1)}
              onToday={handleGoToToday}
            />
            <CalendarRangePanel
              headerLabel="Day"
              days={rangeDays}
              selectedDate={baseDate}
              today={today}
              language={language}
              eventsByDate={allDayEventsByDate}
              allDayEventsByDate={allDayEventsByDate}
              timedEventsByDateHour={timedEventsByDateHour}
              canDragEvents={canDragEvents}
              draggingItem={draggingItem}
              dragOverTargetKey={dragOverTargetKey}
              onAddDate={onAddDate}
              onAddDateTime={onAddDateTime}
              onDropTargetDragOver={onDropTargetDragOver}
              onEventDragEnd={onEventDragEnd}
              onEventDragStart={onEventDragStart}
              onMoveToTarget={onMoveToTarget}
              onNodeClick={onNodeClick}
              onNodeDoubleClick={onNodeDoubleClick}
              onSelectDate={setSelectedDate}
            />
        </div>
    );

});

// Dev Tools
DayView.displayName = 'DayView';

export default DayView;
